import { getEndCursor } from 'components/Blocks/Pagination/paginationHelpers';
import Breadcrumbs from 'components/Layout/Breadcrumbs/Breadcrumbs';
import CommonLayout from 'components/Layout/CommonLayout';
import Webline from 'components/Layout/Webline/Webline';
import BrandDetail from 'components/Pages/BrandDetail/BrandDetail';
import { DEFAULT_PAGE_SIZE } from 'config/constants';
import { mapBrandDetailData } from 'connectors/brands/Brands';
import {
    BrandDetailQueryApi,
    BrandDetailQueryDocumentApi,
    BrandDetailQueryVariablesApi,
    BrandProductsQueryApi,
    BrandProductsQueryDocumentApi,
    BrandProductsQueryVariablesApi,
    useBrandDetailQueryApi,
} from 'graphql/generated';
import { createClient } from 'helpers/createClient';
import { getMappedProductFilter } from 'helpers/filterOptions/getMappedProductFilter';
import { mapParametersFilter } from 'helpers/filterOptions/MapParametersFilter';
import { initServerSideProps, ServerSidePropsType } from 'helpers/InitServerSideProps';
import { getIsRedirectedFromSsr } from 'helpers/isServer';
import { getRedirectWithOffsetPage } from 'helpers/loadMore/getRedirectWithOffsetPage';
import { getProductListSortFromUrlQuery } from 'helpers/parsing/urlParsing';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import {
    FILTER_QUERY_PARAMETER_NAME,
    LOAD_MORE_QUERY_PARAMETER_NAME,
    PAGE_QUERY_PARAMETER_NAME,
    SORT_QUERY_PARAMETER_NAME,
} from 'helpers/queryParams/queryParamNames';
import { useCurrentFilterQuery } from 'helpers/queryParams/useCurrentFilterQuery';
import { useCurrentSortQuery } from 'helpers/queryParams/useCurrentSortQuery';
import { useSeoTitleWithPagination } from 'helpers/seo/useSeoTitleWithPagination';
import { getServerSidePropsWrapper } from 'helpers/serverSide/getServerSidePropsWrapper';
import { handleServerSideErrorResponseForFriendlyUrls } from 'helpers/serverSide/handleServerSideErrorResponseForFriendlyUrls';
import { useQueryError } from 'hooks/graphQl/UseQueryError';
import { useGtmFriendlyPageView } from 'hooks/gtm/useGtmFriendlyPageView';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { NextPageExtended } from 'types/global';
import { CombinedError } from 'urql';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';
import { getNumberFromUrlQuery } from 'utils/getNumberFromUrlQuery';
import { getSlugFromServerSideUrl, getSlugFromUrl } from 'utils/getSlugFromUrl';
import { useGtmPageViewEvent } from 'utils/Gtm/EventFactories';
import { getGtmPageInfoForFriendlyUrl } from 'utils/Gtm/Gtm';

const BrandDetailPage: NextPageExtended<ServerSidePropsType> = () => {
    const router = useRouter();
    const t = useTypedTranslationFunction();
    const slug = getUrlWithoutGetParameters(router.asPath);
    const domainConfig = useDomainConfig();
    const [productLinesList] = getInternationalizedStaticUrls(['/product-lines'], domainConfig.url);
    const currentFilter = useCurrentFilterQuery();
    const currentSort = useCurrentSortQuery();
    const mappedProductFilter = mapParametersFilter(currentFilter);

    const [{ data: brandDetailData, fetching: isBrandDetailFetching, error }] = useBrandDetailQueryApi({
        variables: {
            urlSlug: getSlugFromUrl(slug),
            orderingMode: currentSort,
            filter: mappedProductFilter,
        },
    });

    useQueryError(error, error instanceof CombinedError);

    const mappedBrand = useMemo(() => {
        if (brandDetailData?.brand) {
            return mapBrandDetailData(brandDetailData.brand);
        }

        return null;
    }, [brandDetailData?.brand]);

    const seoTitle = useSeoTitleWithPagination(
        mappedBrand?.productConnection.totalCount,
        mappedBrand?.name,
        mappedBrand?.seoTitle,
    );

    const gtmBrandUrlPageViewEvent = useGtmPageViewEvent(
        getGtmPageInfoForFriendlyUrl(mappedBrand, slug, mappedBrand?.breadcrumb),
    );
    useGtmFriendlyPageView(gtmBrandUrlPageViewEvent, slug, isBrandDetailFetching);

    return (
        <CommonLayout
            showLastVisitedProducts
            isFetchingData={!currentFilter && isBrandDetailFetching && !brandDetailData}
            title={seoTitle}
            description={mappedBrand?.seoMetaDescription}
        >
            {mappedBrand && (
                <Webline>
                    <Breadcrumbs
                        key="breadcrumb"
                        breadcrumb={[
                            { name: t('Overview of product lines'), slug: productLinesList },
                            { name: mappedBrand.name, slug: '' },
                        ]}
                    />
                </Webline>
            )}
            {mappedBrand && <BrandDetail brand={mappedBrand} />}
        </CommonLayout>
    );
};

BrandDetailPage.layout = 'plain';

export const getServerSideProps = getServerSidePropsWrapper(
    ({ domainConfig, redisClient, ssrExchange, t }) =>
        async (context) => {
            const client = createClient({
                publicGraphqlEndpoint: domainConfig.publicGraphqlEndpoint,
                ssrExchange,
                t,
                context,
                redisClient,
            });
            const orderingMode = getProductListSortFromUrlQuery(context.query[SORT_QUERY_PARAMETER_NAME]);
            const filter = getMappedProductFilter(context.query[FILTER_QUERY_PARAMETER_NAME]);
            const page = getNumberFromUrlQuery(context.query[PAGE_QUERY_PARAMETER_NAME], 1);
            const loadMore = getNumberFromUrlQuery(context.query[LOAD_MORE_QUERY_PARAMETER_NAME], 0);
            const urlSlug = getSlugFromServerSideUrl(context.req.url ?? '', context.req.headers);
            const redirect = getRedirectWithOffsetPage(page, loadMore, urlSlug, context.query);

            if (redirect) {
                return redirect;
            }

            const brandDetailResponseResponse = await client
                .query<BrandDetailQueryApi, BrandDetailQueryVariablesApi>(BrandDetailQueryDocumentApi, {
                    urlSlug,
                    filter,
                    orderingMode,
                })
                .toPromise();

            await client
                .query<BrandProductsQueryApi, BrandProductsQueryVariablesApi>(BrandProductsQueryDocumentApi, {
                    endCursor: getEndCursor(page),
                    orderingMode,
                    filter,
                    urlSlug,
                    first: DEFAULT_PAGE_SIZE * (loadMore + 1),
                })
                .toPromise();

            if (getIsRedirectedFromSsr(context.req.headers)) {
                const serverSideErrorResponse = handleServerSideErrorResponseForFriendlyUrls(
                    brandDetailResponseResponse.error?.graphQLErrors,
                    brandDetailResponseResponse.data?.brand,
                    context.res,
                );

                if (serverSideErrorResponse) {
                    return serverSideErrorResponse;
                }
            }

            const initServerSideData = await initServerSideProps({ context, domainConfig, client, ssrExchange });

            return initServerSideData;
        },
);
export default BrandDetailPage;
